'use client' // Error boundaries must be Client Components

import { useEffect } from 'react'
import { reportBoundaryError } from '@/lib/observability'

/**
 * The last resort — a failure in the root layout itself, which `(app)/error.tsx`
 * cannot catch because an error boundary does not wrap the layout above it.
 *
 * This file REPLACES the root layout when active, so it must render its own
 * `<html>` and `<body>`. It also loses `globals.css`, the font links and every
 * design token with it, which is why the styling here is inline and plain
 * rather than reaching for `ErrorState` — a component built on Tailwind tokens
 * would render unstyled at the one moment presentation matters most.
 *
 * ⚠️ `unstable_retry`, not `reset` — see the note in `(app)/error.tsx`.
 */
export default function GlobalError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string }
  unstable_retry: () => void
}) {
  useEffect(() => {
    // See `(app)/error.tsx` — reporting must never be able to throw here, and
    // here least of all: this is the last boundary there is. The port swallows
    // everything and still `console.error`s, so the local signal survives a dead
    // analytics pipe.
    reportBoundaryError(error, { boundary: 'global', digest: error.digest })
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#F8F9FB',
          fontFamily:
            'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          color: '#0F172A',
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: '100%',
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: 16,
            padding: 36,
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>
            CRMPeeps didn&apos;t load
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#64748B', margin: '0 0 24px' }}>
            Something failed before the page could start. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => unstable_retry()}
            style={{
              font: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#FFDA10',
              color: '#000000',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          {error.digest && (
            <p style={{ fontSize: 11, color: '#64748B', marginTop: 20, fontFamily: 'monospace' }}>
              Reference: {error.digest}
            </p>
          )}
        </div>
      </body>
    </html>
  )
}
